import React from 'react';
import _merge from 'lodash/merge';

import { getContext, isAnimationDisabled } from 'common';
import progressBarBackgroundColor from 'common/ProgressBarBackgroundColor';
import ProgressIndicatorAnimation from './ProgressIndicatorAnimation';
import Styled from './styles';

export interface LinearProgressIndicatorDeterminateProps extends React.ComponentPropsWithRef<'div'> {
  /** How much progress is complete. The progress value has to be between 0 and 100. Pass rounded progress value */
  progress: number;
  /** Renders the close button, use "ProgressIndicatorCloseButton" or custom close button */
  renderCloseButton?: React.ReactNode;
  /** Renders the Text, use "ProgressIndicatorText" or custom Label */
  renderText?: React.ReactNode;
  /** If set to 'true' the progress indicator will fade out over 0.3 seconds */
  fade?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: React.CSSProperties
}

/** LinearProgressIndicatorDeterminate, all other props will be provided to the root DOM element */
const LinearProgressIndicatorDeterminate = React.forwardRef<HTMLElement, LinearProgressIndicatorDeterminateProps>((props: LinearProgressIndicatorDeterminateProps, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const {
      progress,
      renderCloseButton,
      renderText,
      fade,
      theme: themeProp,
      disableAnimation: propsdisableAnimation,
      ...otherProps
    } = props;

    const resolvedTheme = _merge(
      {},
      theme ? theme.LINEAR_PROGRESS_DETERMINATE : {},
      themeProp
    );

    const otherStyle = {
      ...props.style,
      width: `${progress}%`,
      background: `rgb(${progressBarBackgroundColor(progress, resolvedTheme)}`,
    };

    return (
      <ProgressIndicatorAnimation
        disableAnimation={isAnimationDisabled(propsdisableAnimation, disableAnimation)}
        fade={fade}
      >
        <Styled.DeterminateContainer {...otherProps} ref={ref} rtl={RTL}>
          <Styled.Wrapper>
            <Styled.ContentWrapper $theme={resolvedTheme}>
              <Styled.ProgressIndicator style={otherStyle} />
              {renderText}
            </Styled.ContentWrapper>
          </Styled.Wrapper>
          {renderCloseButton}
        </Styled.DeterminateContainer>
      </ProgressIndicatorAnimation>
    );
  }));

LinearProgressIndicatorDeterminate.defaultProps = {
  renderCloseButton: undefined,
  renderText: undefined,
  fade: false,
  disableAnimation: undefined,
  theme: {},
  style: {}
};

LinearProgressIndicatorDeterminate.displayName = 'LinearProgressIndicatorDeterminate';

export default LinearProgressIndicatorDeterminate;
